import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { useStyles } from "./styles";
import {
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Button,
  FormControl,
} from "@mui/material";
import {
  formatCreditCardNumber,
  PhoneMask,
  CpfMask,
  dateMask,
  formatCEP,
  RgMask,
  CnpjMask,
  readingDateMask,
} from "../../utils/masks";
import { brazilianStates } from "../../utils/constants";
import flagCards from "../../assets/images/payments.png";
import api from "../../services/api";
import theme from "../../theme";

interface ICPFLHolder {
  type: "pf" | "pj";
  uc: string;
  pn: string;
  reading_date: string;
  holder_name?: string;
  holder_phone?: string;
  holder_cellphone: string;
  holder_email: string;
  holder_birthday?: string;
  holder_document?: string;
  holder_rg?: string;
  zip_code: string;
  state: string;
  city: string;
  street: string;
  number: string;
  neighborhood: string;
  complement: string;
  cnpj?: string;
  inscricao_estadual?: string;
  nome_responsavel?: string;
  cpf_responsavel?: string;
  rg_responsavel?: string;
  data_abertura?: string;
}
export default function TabsMethodPayment({
  payment,
  setPayment,
  value,
  handleChange,
  promotional,
  cardBrandError,
}: any) {
  const classes = useStyles();
  const [options, setOptions] = React.useState<number[]>([]);
  const [selectedValue, setSelectedValue] = React.useState<number>(5);
  const [cpflHolder, setCpflHolder] = React.useState<ICPFLHolder>({
    type: "pf",
    uc: "",
    pn: "",
    reading_date: "",
    holder_cellphone: "",
    holder_email: "",
    zip_code: "",
    state: "",
    city: "",
    street: "",
    number: "",
    neighborhood: "",
    complement: "",
  });
  const [isSearchingCep, setIsSearchingCep] = React.useState(false);
  const previousCpflHolderRef = React.useRef<ICPFLHolder | null>(null);

  React.useEffect(() => {
    if (payment?.cpfl_holder) {
      setCpflHolder(payment.cpfl_holder);
    }
  }, [payment?.cpfl_holder]);

  React.useEffect(() => {
    const currentCpflHolder = cpflHolder;
    const previousCpflHolder = previousCpflHolderRef.current;

    const hasChanged =
      previousCpflHolder === null ||
      Object.keys(currentCpflHolder).some(
        (key) =>
          currentCpflHolder[key as keyof ICPFLHolder] !==
          previousCpflHolder[key as keyof ICPFLHolder]
      );

    if (hasChanged) {
      setPayment((prev: any) => ({
        ...prev,
        cpfl_holder: currentCpflHolder,
      }));
      previousCpflHolderRef.current = currentCpflHolder;
    }
  }, [cpflHolder, setPayment]);

  const getDynamic = async () => {
    try {
      const response = await api.get("/api/user/onboarding/list/bill_due_date");
      setOptions(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    const dynamicValueBoletoDays = localStorage.getItem("selectedDaysBoleto");
    if (dynamicValueBoletoDays) {
      setSelectedValue(Number(dynamicValueBoletoDays));
    }
    getDynamic();
  }, []);

  const handleSelectChange = (e: SelectChangeEvent<number>) => {
    const newValue = e.target.value as number;
    setSelectedValue(newValue);
    localStorage.setItem("selectedDaysBoleto", newValue.toString());
  };

  const obterTipoPagamento = () =>
    payment.payment_type || payment.paymentType || value;

  const handleTypeChange = React.useCallback(
    (newType: "pf" | "pj") => {
      if (newType !== cpflHolder.type) {
        setCpflHolder((prevData) => {
          if (newType === "pf") {
            return {
              ...prevData,
              type: "pf",
              cnpj: "",
              inscricao_estadual: "",
              nome_responsavel: "",
              cpf_responsavel: "",
              rg_responsavel: "",
              data_abertura: "",
              holder_name: prevData.holder_name || "",
              holder_birthday: prevData.holder_birthday || "",
              holder_document: prevData.holder_document || "",
              holder_rg: prevData.holder_rg || "",
              uc: prevData.uc || "",
              pn: prevData.pn || "",
              holder_cellphone: prevData.holder_cellphone || "",
              holder_email: prevData.holder_email || "",
              zip_code: prevData.zip_code || "",
              state: prevData.state || "",
              city: prevData.city || "",
              street: prevData.street || "",
              number: prevData.number || "",
              neighborhood: prevData.neighborhood || "",
              complement: prevData.complement || "",
            };
          } else {
            return {
              ...prevData,
              type: "pj",
              holder_name: "",
              holder_birthday: "",
              holder_document: "",
              holder_rg: "",
              cnpj: prevData.cnpj || "",
              inscricao_estadual: prevData.inscricao_estadual || "",
              nome_responsavel: prevData.nome_responsavel || "",
              cpf_responsavel: prevData.cpf_responsavel || "",
              rg_responsavel: prevData.rg_responsavel || "",
              data_abertura: prevData.data_abertura || "",
              uc: prevData.uc || "",
              pn: prevData.pn || "",
              holder_cellphone: prevData.holder_cellphone || "",
              holder_email: prevData.holder_email || "",
              zip_code: prevData.zip_code || "",
              state: prevData.state || "",
              city: prevData.city || "",
              street: prevData.street || "",
              number: prevData.number || "",
              neighborhood: prevData.neighborhood || "",
              complement: prevData.complement || "",
            };
          }
        });
      }
    },
    [cpflHolder.type]
  );

  const searchPostalCode = async (cep: string) => {
    if (cep && cep.length === 8 && !isSearchingCep) {
      setIsSearchingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();

        if (!data.erro) {
          setCpflHolder((prevData) => ({
            ...prevData,
            zip_code: data.cep,
            street: data.logradouro,
            neighborhood: data.bairro,
            city: data.localidade,
            state: data.uf,
          }));
        }
      } catch (error) {
        console.error("Erro ao buscar o CEP:", error);
      } finally {
        setIsSearchingCep(false);
      }
    }
  };

  const handleInputChange = React.useCallback(
    (field: keyof ICPFLHolder, value: string) => {
      let maskedValue = value;

      switch (field) {
        case "rg_responsavel":
          maskedValue = RgMask(value);
          break;
        case "holder_document":
        case "cpf_responsavel":
          maskedValue = CpfMask(value);
          break;
        case "holder_phone":
        case "holder_cellphone":
          maskedValue = PhoneMask(value);
          break;
        case "holder_birthday":
        case "data_abertura":
          maskedValue = dateMask(value);
          break;
        case "zip_code":
          maskedValue = formatCEP(value);
          if (maskedValue.replace(/\D/g, "").length === 8) {
            searchPostalCode(maskedValue.replace(/\D/g, ""));
          }
          break;
        case "cnpj":
          maskedValue = CnpjMask(value);
          break;
      }

      setCpflHolder((prev) => ({
        ...prev,
        [field]: maskedValue,
      }));
    },
    []
  );

  return (
    <div>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          p: 2,
          backgroundColor: theme.palette.primary.main,
          color: "#FFF",
          borderRadius: "4px 4px 0 0",
        }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: "bold" }}>
          {(() => {
            const paymentType = obterTipoPagamento();

            switch (paymentType) {
              case "boleto":
                return "Boleto Bancário";
              case "cpfl":
                return "Pagamento CPFL";
              case "pix_automatico":
                return "Pix Automático";
              default:
                return "Cartão de crédito";
            }
          })()}
        </Typography>
        {promotional && (
          <Typography sx={{ fontSize: "14px", mt: 1 }}>
            * Primeiro pagamento em 30 dias
          </Typography>
        )}
      </Box>

      {(obterTipoPagamento() === "credit_card") && (
        <Box>
          <Typography className={classes.boxTitle}>
            Informações do Cartão de Crédito
          </Typography>
          <Box className={classes.separator} />
          <label className={classes.label}>Nome titular do cartão</label>
          <TextField
            name="name"
            id="outlined-basic"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ marginTop: "5px", marginBottom: "20px" }}
            value={payment?.card_holder_name}
            onChange={(e) =>
              setPayment({
                ...payment,
                card_holder_name: e.target.value,
              })
            }
          />
          <label className={classes.label}>Número do cartão</label>
          <TextField
            name="cardNumber"
            id="outlined-basic"
            variant="outlined"
            size="small"
            fullWidth
            value={
              formatCreditCardNumber(payment?.card_number) ??
              payment?.card_number
            }
            onChange={(e) => {
              setPayment({ ...payment, card_number: e.target.value });
            }}
            inputProps={{ maxLength: 19 }}
            error={!!cardBrandError}
            helperText={cardBrandError || ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-error fieldset": {
                  borderColor: "red",
                  borderWidth: 2,
                },
              },
            }}
          />
          <Box className={classes.containerCards}>
            <img
              alt="Bandeiras do cartao de credito"
              src={flagCards}
              className={classes.imgCards}
            />
          </Box>
          <Box className={classes.divRow}>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>Mês de expiração</label>
              <TextField
                name="expired"
                id="outlined-basic"
                variant="outlined"
                size="small"
                fullWidth
                sx={{ marginTop: "5px", marginBottom: "20px" }}
                value={payment?.card_month_expiration}
                onChange={(e) =>
                  setPayment({
                    ...payment,
                    card_month_expiration: e.target.value,
                  })
                }
                inputProps={{ maxLength: 2 }}
              />
            </Box>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>Ano de expiração</label>
              <TextField
                name="expired"
                id="outlined-basic"
                variant="outlined"
                size="small"
                fullWidth
                sx={{ marginTop: "5px", marginBottom: "20px" }}
                value={payment?.card_year_expiration}
                onChange={(e) =>
                  setPayment({
                    ...payment,
                    card_year_expiration: e.target.value,
                  })
                }
                inputProps={{ maxLength: 2 }}
              />
            </Box>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>CVV</label>
              <TextField
                name="cvv"
                id="outlined-basic"
                variant="outlined"
                size="small"
                fullWidth
                sx={{ marginTop: "5px", marginBottom: "20px" }}
                value={payment?.card_cvv}
                onChange={(e) =>
                  setPayment({ ...payment, card_cvv: e.target.value })
                }
                inputProps={{ maxLength: 4 }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {(obterTipoPagamento() === "boleto") && (
        <Box>
          <Typography className={classes.descriptionTile}>
            O pagamento via boleto pode levar até 72 horas para ser compensado,
            e a ativação do plano será realizada automaticamente após a
            confirmação.
          </Typography>
          <Box className={classes.selectContentDays}>
            <label className={classes.labelSelectDays}>
              Selecione o dia do vencimento mensal do boleto bancário:{" "}
            </label>
            <Select
              name="expired"
              id="outlined-basic"
              variant="outlined"
              size="small"
              value={selectedValue}
              onChange={handleSelectChange}
              sx={{
                marginTop: "10px",
                marginBottom: "20px",
                width: "100%",
              }}
            >
              <MenuItem
                value={0}
                disabled
              >
                Selecione o dia do vencimento
              </MenuItem>
              {options.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      )}

      {(obterTipoPagamento() === "pix_automatico") && (
        <Box>
          <Typography className={classes.descriptionTile}>
            O pagamento via Pix Automático será recorrente mensalmente. Após
            escanear o QR Code e autorizar, sua assinatura será ativada
            automaticamente.
          </Typography>
        </Box>
      )}

      {(obterTipoPagamento() === "cpfl") && (
        <Box>
          <Typography className={classes.descriptionTile}>
            Para pagamentos via CPFL, a conclusão cadastral será feita junto à
            central Dr. Saúde, sendo obrigatória a apresentação presencial dos
            seguintes documentos: cópia da conta atual, documento com foto do
            titular da conta e autorização de cobrança da CPFL.
          </Typography>
          <Box className={classes.separator} />

          <Box className={classes.divRow}>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>
                Tipo do Titular <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                value={cpflHolder.type}
                onChange={(e) =>
                  handleTypeChange(e.target.value as "pf" | "pj")
                }
                fullWidth
              >
                <MenuItem value="pf">Pessoa Física</MenuItem>
                <MenuItem value="pj">Pessoa Jurídica</MenuItem>
              </Select>
            </Box>
          </Box>

          <Box className={classes.divRow}>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>
                UC <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                value={cpflHolder.uc}
                onChange={(e) =>
                  setCpflHolder({ ...cpflHolder, uc: e.target.value })
                }
                fullWidth
              />
            </Box>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>
                PN <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                value={cpflHolder.pn}
                onChange={(e) =>
                  setCpflHolder({ ...cpflHolder, pn: e.target.value })
                }
                fullWidth
              />
            </Box>
          </Box>

          {cpflHolder.type === "pf" ? (
            <>
              <Box className={classes.divRow}>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    Nome do Titular <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={cpflHolder.holder_name}
                    onChange={(e) =>
                      setCpflHolder({
                        ...cpflHolder,
                        holder_name: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Box>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    E-mail do Titular <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={cpflHolder.holder_email}
                    onChange={(e) =>
                      setCpflHolder({
                        ...cpflHolder,
                        holder_email: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Box>
              </Box>

              <Box className={classes.divRow}>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>Telefone do Titular</label>
                  <TextField
                    value={PhoneMask(cpflHolder.holder_phone || "")}
                    onChange={(e) =>
                      handleInputChange("holder_phone", e.target.value)
                    }
                    fullWidth
                    placeholder="(00) 0000-0000"
                    inputProps={{ maxLength: 14 }}
                  />
                </Box>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    Celular do Titular <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={PhoneMask(cpflHolder.holder_cellphone || "")}
                    onChange={(e) =>
                      handleInputChange("holder_cellphone", e.target.value)
                    }
                    fullWidth
                    placeholder="(00) 00000-0000"
                    inputProps={{ maxLength: 15 }}
                  />
                </Box>
              </Box>

              <Box className={classes.divRow}>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    CPF do Titular <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={CpfMask(cpflHolder.holder_document || "")}
                    onChange={(e) =>
                      handleInputChange("holder_document", e.target.value)
                    }
                    fullWidth
                    placeholder="000.000.000-00"
                    inputProps={{ maxLength: 14 }}
                  />
                </Box>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    Aniversário do Titular{" "}
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={dateMask(cpflHolder.holder_birthday || "")}
                    onChange={(e) =>
                      handleInputChange("holder_birthday", e.target.value)
                    }
                    fullWidth
                    placeholder="DD/MM/AAAA"
                    inputProps={{ maxLength: 10 }}
                  />
                </Box>
              </Box>

              <Box className={classes.divRow}>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    RG do Titular <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={RgMask(cpflHolder.holder_rg || "")}
                    onChange={(e) =>
                      handleInputChange("holder_rg", e.target.value)
                    }
                    fullWidth
                    placeholder="00.000.000-0"
                    inputProps={{ maxLength: 12 }}
                  />
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Box className={classes.divRow}>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    CNPJ <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={CnpjMask(cpflHolder.cnpj || "")}
                    onChange={(e) => handleInputChange("cnpj", e.target.value)}
                    fullWidth
                    placeholder="00.000.000/0000-00"
                    inputProps={{ maxLength: 18 }}
                  />
                </Box>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    Inscrição Estadual <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={cpflHolder.inscricao_estadual}
                    onChange={(e) =>
                      setCpflHolder({
                        ...cpflHolder,
                        inscricao_estadual: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Box>
              </Box>

              <Box className={classes.divRow}>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    Nome do Responsável <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={cpflHolder.nome_responsavel}
                    onChange={(e) =>
                      setCpflHolder({
                        ...cpflHolder,
                        nome_responsavel: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Box>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    E-mail do Titular <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={cpflHolder.holder_email}
                    onChange={(e) =>
                      setCpflHolder({
                        ...cpflHolder,
                        holder_email: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Box>
              </Box>

              <Box className={classes.divRow}>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>Telefone do Titular</label>
                  <TextField
                    value={PhoneMask(cpflHolder.holder_phone || "")}
                    onChange={(e) =>
                      handleInputChange("holder_phone", e.target.value)
                    }
                    fullWidth
                    placeholder="(00) 0000-0000"
                    inputProps={{ maxLength: 14 }}
                  />
                </Box>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    Celular do Titular <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={PhoneMask(cpflHolder.holder_cellphone || "")}
                    onChange={(e) =>
                      handleInputChange("holder_cellphone", e.target.value)
                    }
                    fullWidth
                    placeholder="(00) 00000-0000"
                    inputProps={{ maxLength: 15 }}
                  />
                </Box>
              </Box>

              <Box className={classes.divRow}>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    CPF do Responsável <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={CpfMask(cpflHolder.cpf_responsavel || "")}
                    onChange={(e) =>
                      handleInputChange("cpf_responsavel", e.target.value)
                    }
                    fullWidth
                    placeholder="000.000.000-00"
                    inputProps={{ maxLength: 14 }}
                  />
                </Box>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    RG do Responsável <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={RgMask(cpflHolder.rg_responsavel || "")}
                    onChange={(e) =>
                      handleInputChange("rg_responsavel", e.target.value)
                    }
                    fullWidth
                    placeholder="00.000.000-0"
                    inputProps={{ maxLength: 12 }}
                  />
                </Box>
              </Box>

              <Box className={classes.divRow}>
                <Box className={classes.labelAndInput}>
                  <label className={classes.label}>
                    Data de Abertura <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    value={dateMask(cpflHolder.data_abertura || "")}
                    onChange={(e) =>
                      handleInputChange("data_abertura", e.target.value)
                    }
                    fullWidth
                    placeholder="DD/MM/AAAA"
                    inputProps={{ maxLength: 10 }}
                  />
                </Box>
              </Box>
            </>
          )}

          <Box className={classes.divRow}>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>Leitura da data:</label>
              <TextField
                value={readingDateMask(cpflHolder.reading_date)}
                onChange={(e) =>
                  handleInputChange("reading_date", e.target.value)
                }
                fullWidth
                placeholder="1 ao 31"
                inputProps={{ maxLength: 2 }}
              />
            </Box>
          </Box>

          <Box className={classes.divRow}>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>CEP</label>
              <TextField
                value={cpflHolder.zip_code}
                onChange={(e) => handleInputChange("zip_code", e.target.value)}
                fullWidth
                placeholder="00000-000"
                inputProps={{ maxLength: 9 }}
              />
            </Box>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>Número</label>
              <TextField
                value={cpflHolder.number}
                onChange={(e) =>
                  setCpflHolder({ ...cpflHolder, number: e.target.value })
                }
                fullWidth
              />
            </Box>
          </Box>
          <Box className={classes.divRow}>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>Endereço</label>
              <TextField
                value={cpflHolder.street}
                onChange={(e) =>
                  setCpflHolder({ ...cpflHolder, street: e.target.value })
                }
                fullWidth
              />
            </Box>
          </Box>
          <Box className={classes.divRow}>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>Bairro</label>
              <TextField
                value={cpflHolder.neighborhood}
                onChange={(e) =>
                  setCpflHolder({ ...cpflHolder, neighborhood: e.target.value })
                }
                fullWidth
              />
            </Box>
          </Box>
          <Box className={classes.divRow}>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>Complemento</label>
              <TextField
                value={cpflHolder.complement}
                onChange={(e) =>
                  setCpflHolder({ ...cpflHolder, complement: e.target.value })
                }
                fullWidth
              />
            </Box>
          </Box>

          <Box className={classes.divRow}>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>Cidade</label>
              <TextField
                value={cpflHolder.city}
                fullWidth
              />
            </Box>
            <Box className={classes.labelAndInput}>
              <label className={classes.label}>Estado</label>
              <FormControl
                fullWidth
                size="small"
              >
                <Select
                  value={cpflHolder.state || ""}
                  variant="outlined"
                  style={{ height: "45px" }}
                >
                  <MenuItem value="">Selecione um estado</MenuItem>
                  {brazilianStates.map((state) => (
                    <MenuItem
                      key={state.code}
                      value={state.code}
                    >
                      {state.code} - {state.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
}
